import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@shared/lib/cn';

export type CardVariant = 'default' | 'highlight' | 'gold' | 'danger';

const VARIANTS: Record<CardVariant, string> = {
  default: 'border border-border-subtle bg-surface',
  // "Next class": 2 px primary border on the start side (flips in RTL).
  highlight: 'border border-border-subtle border-s-2 border-s-primary bg-surface',
  gold: 'border border-gold-500 bg-accent-subtle',
  danger: 'border border-danger bg-danger-subtle',
};

export interface CardProps extends HTMLAttributes<HTMLElement> {
  variant?: CardVariant;
  as?: 'div' | 'section' | 'article';
  padded?: boolean;
}

export function Card({ variant = 'default', as: Tag = 'div', padded = true, className, ...rest }: CardProps) {
  return <Tag className={cn('rounded-lg', VARIANTS[variant], padded && 'p-4 md:p-6', className)} {...rest} />;
}

export interface CardHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  as?: 'h2' | 'h3' | 'h4';
  className?: string;
}

export function CardHeader({ title, description, actions, as: Heading = 'h3', className }: CardHeaderProps) {
  return (
    <div className={cn('mb-4 flex flex-wrap items-start justify-between gap-3', className)}>
      <div className="min-w-0">
        <Heading className="text-lg">{title}</Heading>
        {description ? <p className="mt-1 text-sm text-muted">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}
